import * as React from 'react';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import Icon from '@adecco/base-app/src/components/atoms/Icon/Icon';
import TextField from '@adecco/base-app/src/components/atoms/TextField/TextField';
import Button from '@adecco/base-app/src/components/atoms/Button/Button';
import calcrem from '@adecco/base-app/src/utilities/calcrem/calcrem';
import { moreThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import DisLogo from '@adecco/dis-app/src/components/atoms/Icon/Icons/DisLogo';
import { useAppContext } from '@adecco/base-app/src/utilities/appProvider/appProvider';
import Link from 'next/link';
import { useDataLayerPushEvent } from '@adecco/base-app/src/hooks/useDataLayerPushEvent';

interface IPageHead {
  compact?: boolean;
  className?: string;
}

const PageHeadPlaceholder = styled.div<{
  compact?: boolean;
}>`
  height: 88px;
  margin-bottom: ${(props) => props.theme.margins.pageHead};

  ${moreThan('lg')} {
    height: ${(props) =>
      props.compact === true
        ? props.theme.headerSpacing.searchBoxFakeHeight
        : props.theme.headerSpacing.searchBoxFakeHeightWithSearch};
    margin-bottom: ${(props) => props.theme.margins.pageHeadWideScreen};
  }
  @media print {
    display: none;
  }
`;

const PageHeadStyledContainer = styled.nav<IPageHead>`
  background-color: #ad0f28;
  box-shadow: ${(props) => props.theme.boxShadow.pageHeadContainer};
  padding: ${(props) => props.theme.headerSpacing.logoHeight} 20px;
  position: fixed;
  width: 100%;
  z-index: ${(props) => props.theme.zIndex.header};
  top: 0;
  transition: transform 0.25s ease-in-out;

  ${moreThan('md')} {
    border-radius: ${(props) => props.theme.radius.pageHead};
    box-shadow: ${(props) => props.theme.boxShadow.pageHead};
  }

  ${moreThan('lg')} {
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 250px;
    height: 110px;
    margin: auto 0;
    padding: 35px;
  }
`;

const PageHeadStyledContent = styled.ul<IPageHead>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${moreThan('md')} {
    max-width: ${calcrem(1120)};
    margin: 0 auto;
    width: 100%;
  }
`;

const PageHeadLogo = styled.div<IPageHead>`
  height: ${(props) => props.theme.headerSpacing.logoHeight};
  display: flex;
  align-items: center;
`;

const SearchBox = styled.form<{
  showOnXs: boolean;
}>`
  min-height: 110px;
  display: block;
  width: 100%;
  position: fixed;
  top: 60px;
  transition: transform 0.5s ease-in-out;
  transform: ${(props) => (props.showOnXs ? 'translateY(0)' : 'translateY(-240px)')};
  z-index: ${(props) => props.theme.zIndex.searchBox};
  background: ${(props) => props.theme.backgrounds.searchBox};

  ${moreThan('lg')} {
    transition: transition 0s;
    padding-left: 250px;
    transform: translateY(-60px);
  }
`;

const SearchBoxContent = styled.fieldset<IPageHead>`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0 auto;
  max-width: ${calcrem(1120)};
  padding: ${(props) => props.theme.headerSpacing.searchBoxPaddingTop} 15px
    ${(props) => props.theme.headerSpacing.searchBoxPaddingBottom};

  label {
    color: ${(props) => props.theme.colors.white};
  }

  input {
    margin-bottom: ${(props) => props.theme.margins.searchBoxInput};
  }

  ${moreThan('md')} {
    flex-direction: row;
    align-items: flex-end;
    align-content: flex-start;

    input {
      margin-bottom: ${(props) => props.theme.margins.searchBoxInputWideScreen};
    }
  }

  > * {
    width: auto;
    margin-right: ${(props) => props.theme.margins.searchBoxChildren};
    padding: 0;
  }
`;

const SearchTextField = styled(TextField)<IPageHead>`
  height: ${(props) => props.theme.headerSpacing.inputCustomHeight};
  padding: 12px 24px 10px 24px;
  font-family: ${(props) => props.theme.fonts.searchTextField};

  ${moreThan('md')} {
    transition: width 0.5s ease-in-out;
    width: 280px;
  }

  ${moreThan('maxWidth')} {
    width: 360px;
  }

  ::placeholder,
  ::-webkit-input-placeholder {
    font-family: ${(props) => props.theme.fonts.searchTextField};
    color: ${(props) => props.theme.colors.black};
  }
  :-ms-input-placeholder {
    font-family: ${(props) => props.theme.fonts.searchTextField};
    color: ${(props) => props.theme.colors.black};
  }
`;

const SearchSubmitButton = styled(Button)<IPageHead>`
  border: ${(props) => props.theme.borders.searchButton};
  border-radius: ${(props) => props.theme.radius.panel};
  color: ${(props) => props.theme.colors.white};
  height: ${(props) => props.theme.headerSpacing.inputCustomHeight};
  overflow: visible;
  white-space: nowrap;
  padding: 0 10px;
  width: 100px;
  font-size: ${(props) => props.theme.fontSizes.searchSubmitButton};

  ${moreThan('md')} {
    padding: 0 40px;
    width: auto;
  }
`;

const BackToSearchMobileLink = styled.button`
  display: inline-block;
  height: calc(
    ${(props) => props.theme.headerSpacing.logoHeight} +
      ${(props) => props.theme.headerSpacing.searchIconExtraPadding}
  );
  padding: ${(props) => props.theme.headerSpacing.searchIconExtraPadding};
  width: calc(
    ${(props) => props.theme.headerSpacing.logoHeight} +
      ${(props) => props.theme.headerSpacing.searchIconExtraPadding}
  );
  border: none;
  background: transparent;

  ${moreThan('lg')} {
    display: none;
  }
`;

const PageHead: React.FunctionComponent<IPageHead> = ({ compact = false, className }) => {
  const [showOnXs, setShowOnXs] = useState(false);
  const { handleSubmit, register } = useForm();
  const { t, logoLink } = useAppContext();
  const searchUrl = t('header-job-search-url') ?? '';
  const [queryValue, setQueryValue] = useState('');
  const [urlValue, setUrlValue] = useState('');

  const defaultQueryValue = 'no selection made';

  useDataLayerPushEvent({
    event: 'ga_event',
    data: {
      event_category: 'search jobs',
      event_action: queryValue === defaultQueryValue ? queryValue : 'search_banner',
      search_keyword: queryValue,
    },
    deps: [queryValue],
    checker: queryValue !== '',
  });

  const onSubmit = (values: FieldValues) => {
    setQueryValue(values.query || defaultQueryValue);

    let url = searchUrl;

    if (values.searchLocation && !values.query) {
      url += `?k=&l=${values.searchLocation}&display=15`;
    } else if (!values.searchLocation && values.query) {
      url += `?k=${values.query}&l=&display=15`;
    } else {
      url += `?k=${values.query}&l=${values.searchLocation}&display=15`;
    }

    setUrlValue(url);
  };

  useEffect(() => {
    if (urlValue) {
      window.location.href = urlValue;
    }
  }, [urlValue]);

  return (
    <>
      <header>
        <PageHeadStyledContainer>
          <PageHeadStyledContent>
            <li>
              <PageHeadLogo>
                {logoLink ? (
                  <Link href={logoLink}>
                    <DisLogo />
                  </Link>
                ) : (
                  <DisLogo />
                )}
              </PageHeadLogo>
            </li>
            <li>
              <BackToSearchMobileLink onClick={() => setShowOnXs(!showOnXs)}>
                {showOnXs ? (
                  <Icon name="close-x" fill="white" />
                ) : (
                  <Icon name="search" fill="white" />
                )}
              </BackToSearchMobileLink>
            </li>
          </PageHeadStyledContent>
        </PageHeadStyledContainer>
      </header>
      <SearchBox method="get" onSubmit={handleSubmit(onSubmit)} showOnXs={showOnXs}>
        <SearchBoxContent>
          <SearchTextField
            name="query"
            register={register('query', { required: false })}
            placeholder={t('page-header-input-placeholder-jobtitle')}
            noPaddingBottom
          />
          <SearchTextField
            name="searchLocation"
            register={register('searchLocation', { required: false })}
            placeholder={t('page-header-input-placeholder-zip')}
            noPaddingBottom
          />
          <SearchSubmitButton type="button" variant="small-ghost">
            {t('form-button-search')}
          </SearchSubmitButton>
        </SearchBoxContent>
      </SearchBox>
      <PageHeadPlaceholder className={className} compact={compact} />
    </>
  );
};

export default PageHead;
